import { Timeout } from 'ahooks/lib/useRequest/src/types';
import { useRef, useMemo, useEffect, useState } from 'react';
import { FrontWebConfigKeyType } from './useFrontWebConfig/defaultConfig';
import DBO_FWC from './useFrontWebConfig';
import _ from 'lodash-es';
import { useStateRef } from './common';
import { DBO_TM } from '@op/utils/format';

function getUtcDate() {
  const timezone = DBO_TM.timezone();
  return DBO_TM.userUtc(timezone).format('YYYY-MM-DD HH:mm:ss');
}

export default function useRefresh({
  key,
  defaultRefreshTask = {},
}: {
  key: FrontWebConfigKeyType;
  defaultRefreshTask?: { [key: string]: () => Promise<void> };
}) {
  const [date, setDate] = useState(getUtcDate());
  const [timeRef, time, setTime] = useStateRef(0);
  const refreshTaskList = useRef<{ [key: string]: () => Promise<void> }>(
    defaultRefreshTask,
  );
  function addRefreshTask(key: string, func: () => Promise<void>) {
    refreshTaskList.current[key] = func;
  }

  function executeRefreshTask() {
    return Promise.all(
      Object.values(refreshTaskList.current).map(item => item?.()),
    );
  }

  const timerRef = useRef<Timeout | null>(null);
  const spaceTime = useMemo(() => {
    const spaceTime = Number(DBO_FWC.get(key));
    return isNaN(spaceTime) ? 0 : spaceTime;
  }, []);

  function openTimeout() {
    if (timerRef.current === null) {
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      timerRef.current = setInterval(async () => {
        const realValue = (timeRef.current! + 1) % spaceTime;
        if (realValue === 0) {
          await executeRefreshTask();
          setDate(getUtcDate());
        }
        setTime(realValue);
      }, 60 * 1000 * 1);
    }
  }

  function clearTimer() {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }

  function initTimer(clearTime = true) {
    if (clearTime) {
      setTime(0);
    }
    if (spaceTime >= 1) {
      openTimeout();
    }
  }

  useEffect(() => {
    return () => clearTimer();
  }, []);

  return {
    time,
    date,
    addRefreshTask,
    initTimer,
  };
}
